/**
 * Invoicing block — the framed invoice section inside the designation
 * detail panel.
 *
 * Lists the designation's invoices (date order) with number, type,
 * amount and issue/payment dates, then three totals:
 *   • Already invoiced  = Σ amounts (EUR)        — `sumInvoicesInEUR`
 *   • Invoiced %        = invoiced / ordered     — `invoicedPercent`
 *   • RI (remaining)    = ordered − invoiced ≥ 0 — `remainingToInvoice`
 *
 * Soft 5-invoice cap: the add button disables at 5 (deposits — UI
 * guard only, no Firestore rule). CRUD buttons are finance-only,
 * mirroring `firestore.rules`.
 */

import { Box, Button, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/AddOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useTranslation } from 'react-i18next';
import type { Designation, Invoice } from '@altyor-kronos/shared-types';
import { colors } from '../../core/theme/theme';
import { formatCurrencyEUR, formatNumber } from './finance';
import { invoicedPercent, remainingToInvoice, sumInvoicesInEUR } from './invoiceMath';

export const MAX_INVOICES_PER_DESIGNATION = 5;

export interface InvoiceRow {
  id: string;
  data: Invoice;
}

const TYPE_CHIP_SX: Record<Invoice['type'], { bgcolor: string; color: string }> = {
  prepayment: { bgcolor: colors.warmBeige, color: colors.bodyText },
  final: { bgcolor: colors.softGreen, color: colors.bodyText },
  bpi: { bgcolor: colors.lightGray, color: colors.bodyText },
};

function formatDate(ts: Invoice['invoiceDate'] | undefined, locale: string): string {
  if (!ts) return '—';
  return ts.toDate().toLocaleDateString(locale);
}

export default function InvoicesBlock({
  designation,
  invoices,
  locale,
  canManageInvoices,
  onAdd,
  onEdit,
  onDelete,
}: {
  designation: Designation;
  invoices: readonly InvoiceRow[];
  locale: string;
  canManageInvoices: boolean;
  onAdd: () => void;
  onEdit: (invoice: InvoiceRow) => void;
  onDelete: (invoice: InvoiceRow) => void;
}) {
  const { t } = useTranslation();

  const sorted = [...invoices].sort(
    (a, b) => a.data.invoiceDate.toMillis() - b.data.invoiceDate.toMillis(),
  );
  const alreadyInvoiced = sumInvoicesInEUR(sorted.map((r) => r.data));
  const percent = invoicedPercent(alreadyInvoiced, designation.orderedAmount ?? 0);
  const remaining = remainingToInvoice(designation.orderedAmount ?? 0, alreadyInvoiced);
  const maxReached = sorted.length >= MAX_INVOICES_PER_DESIGNATION;

  return (
    <Box
      sx={{ border: `1px solid ${colors.lightGray}`, p: 1.5 }}
      data-testid="finance-invoices-block"
    >
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {t('finance.invoicing.title')}
        </Typography>
        {canManageInvoices && (
          <Tooltip title={maxReached ? t('finance.invoicing.max') : ''}>
            <span>
              <Button
                size="small"
                variant="text"
                startIcon={<AddIcon fontSize="small" />}
                onClick={onAdd}
                disabled={maxReached}
                data-testid={`finance-add-invoice-${designation.id}`}
              >
                {t('finance.invoicing.add')}
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>

      {sorted.length === 0 ? (
        <Typography variant="caption" sx={{ color: colors.textMuted }}>
          {t('finance.invoicing.empty')}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {sorted.map((row) => (
            <Box
              key={row.id}
              data-testid={`finance-invoice-row-${row.id}`}
              sx={{ borderBottom: `1px solid ${colors.lightGray}`, pb: 0.75 }}
            >
              <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, flex: '1 1 120px', minWidth: 0 }} noWrap>
                  {row.data.invoiceNumber}
                </Typography>
                <Chip
                  size="small"
                  label={t(`finance.invoice.types.${row.data.type}`)}
                  sx={TYPE_CHIP_SX[row.data.type] ?? TYPE_CHIP_SX.final}
                />
                <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {formatCurrencyEUR(row.data.invoiceAmount, locale)}
                </Typography>
                {canManageInvoices && (
                  <>
                    <IconButton
                      size="small"
                      aria-label={t('common.edit')}
                      onClick={() => onEdit(row)}
                      data-testid={`finance-invoice-edit-${row.id}`}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label={t('common.delete')}
                      onClick={() => onDelete(row)}
                      data-testid={`finance-invoice-delete-${row.id}`}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Stack>
              <Typography variant="caption" sx={{ color: colors.textMuted }}>
                {t('finance.invoicing.issuedOn', {
                  date: formatDate(row.data.invoiceDate, locale),
                })}
                {' · '}
                {row.data.paymentDate
                  ? t('finance.invoicing.paidOn', {
                      date: formatDate(row.data.paymentDate, locale),
                    })
                  : t('finance.invoicing.unpaid')}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}

      <Stack spacing={0.5} sx={{ mt: 1.5 }}>
        <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
          <Typography variant="body2" sx={{ color: colors.textMuted }}>
            {t('finance.invoicing.alreadyInvoiced')}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            <span data-testid="finance-invoiced-total">
              {formatCurrencyEUR(alreadyInvoiced, locale)}
            </span>{' '}
            <Typography
              component="span"
              variant="body2"
              sx={{ color: colors.textMuted }}
              data-testid="finance-invoiced-percent"
            >
              ({formatNumber(percent, locale)}%)
            </Typography>
          </Typography>
        </Stack>
        <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
          <Typography variant="body2" sx={{ color: colors.textMuted }}>
            {t('finance.invoicing.remaining')}
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700 }}
            data-testid="finance-invoiced-remaining"
          >
            {formatCurrencyEUR(remaining, locale)}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}
